import _ from 'lodash';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Team } from '../../interfaces/team';
import { PersonalUser } from '../../interfaces/account';
// import ActionError from '../../utils/ActionError';
import { ErrorType, SerializableError } from '../../utils/errorHandling';
import {
  createTeam,
  deleteTeam,
  renameTeam,
  fetchAllAccounts,
  selectAccount,
} from '../ActionTypes/teamActionTypes';

export interface OrganizationState {
  personalAccount: {
    record: PersonalUser | null;
    loading: boolean;
    error: SerializableError | null;
  };

  allTeams: {
    records: Team[];
    loading: boolean;
    error: SerializableError | null;
  };
  currentTeam: {
    record: Team | null;
    loading: boolean;
    error: SerializableError | null;
  };
  newTeam: {
    record: Team | null;
    loading: boolean;
    error: SerializableError | null;
  };
  deletedTeamsById: Record<
    string,
    {
      record: Team | null;
      loading: boolean;
      error: SerializableError | null;
    }
  >;
  renamedTeamsById: Record<
    string,
    {
      record: Team | null;
      loading: boolean;
      error: SerializableError | null;
    }
  >;
  isAccountSwitching: boolean;
}

const initialState: OrganizationState = {
  personalAccount: {
    record: null,
    loading: false,
    error: null,
  },
  allTeams: {
    records: [],
    loading: false,
    error: null,
  },
  currentTeam: {
    record: null,
    loading: false,
    error: null,
  },
  newTeam: {
    record: null,
    loading: false,
    error: null,
  },
  deletedTeamsById: {},
  renamedTeamsById: {},
  isAccountSwitching: false,
};

export const LS_KEY_CURRENT_ACCOUNT_ID = 'currentAccountId';

const _getCurrentAccountInLS = () => {
  const str = localStorage.getItem(LS_KEY_CURRENT_ACCOUNT_ID) || '';
  return str.split(':');
};

const _setCurrentAccountInLS = (
  cirraOrganizationId: string | null | undefined,
  state: OrganizationState
) => {
  const val = cirraOrganizationId
    ? `team:${cirraOrganizationId}`
    : `personal:${state.personalAccount.record?.cirra_user_id}`;

  localStorage.setItem(LS_KEY_CURRENT_ACCOUNT_ID, val);
};

const _setCurrentAccount = (
  state: OrganizationState,
  action: PayloadAction<Team | null>
) => {
  const team = action.payload;
  state.currentTeam.record = team;
  state.currentTeam.loading = false;
  const teamId = team?.cirra_organization_id || null;

  _setCurrentAccountInLS(teamId, state);
  if (state.personalAccount.record) {
    state.personalAccount.record.most_recent_cirra_organization_id = teamId;
  }
};

const _initCurrentAccount = (state: OrganizationState) => {
  const personalAccount = state.personalAccount.record;
  if (personalAccount) {
    // Always use the backend value as the source of truth on app load/refresh.
    // This ensures cross-device consistency - the backend's most_recent_cirra_organization_id
    // is the authoritative source. localStorage is only used within a session after switching.
    const teamIdFromBe = personalAccount.most_recent_cirra_organization_id;
    const [typeFromLs] = _getCurrentAccountInLS();

    // If localStorage indicates personal account AND backend has no team, respect that.
    // This handles the case where user explicitly chose personal account.
    // Exception: team-only users must always be in a team context.
    if (
      typeFromLs === 'personal' &&
      !teamIdFromBe &&
      !personalAccount.team_only
    ) {
      _setCurrentAccountInLS(null, state);
      return;
    }

    // Use backend value as source of truth
    const teamId = teamIdFromBe;

    // Sync localStorage with backend value
    _setCurrentAccountInLS(teamId, state);

    // Find the team record (or null if switching to personal account)
    let team = teamId
      ? state.allTeams.records.find(
          (t: Team) => t.cirra_organization_id === teamId
        ) || null
      : null;

    // Team-only users must always be in a team context.
    // If no team was found, auto-select the first accessible team.
    // If no accessible team exists, fall back to the first team to avoid
    // a broken state where the user has no selectable context.
    if (
      !team &&
      personalAccount.team_only &&
      state.allTeams.records.length > 0
    ) {
      team =
        state.allTeams.records.find((t: Team) => Boolean(t.current_user)) ||
        state.allTeams.records[0];
    }

    _setCurrentAccount(state, {
      payload: team,
      type: 'team/setCurrentTeam',
    } as PayloadAction<Team | null>);
  }
};

const _formatTeamObject = (team: Team) => {
  return {
    ...team,
    current_user: team?.current_user
      ? {
          ...team.current_user,
          team: _.omit(team, ['current_user']),
        }
      : team?.current_user,
  };
};

const teamSlice = createSlice({
  name: 'team',
  initialState,
  reducers: {
    setCurrentTeam: _setCurrentAccount,
  },
  extraReducers: (builder) => {
    builder

      // Fetch all accounts
      .addCase(fetchAllAccounts.pending, (state) => {
        state.allTeams.loading = true;
        state.allTeams.error = null;
        state.personalAccount.loading = true;
        state.personalAccount.error = null;
      })
      .addCase(fetchAllAccounts.fulfilled, (state, action) => {
        const { personalAccount, allTeams } = action.payload;
        state.allTeams.loading = false;
        state.allTeams.records = (allTeams as Team[]).map(_formatTeamObject);
        state.personalAccount.loading = false;
        state.personalAccount.record = personalAccount;
        _initCurrentAccount(state);
      })
      .addCase(fetchAllAccounts.rejected, (state, action) => {
        const payload = (action.payload || {}) as { message?: string };
        const error: SerializableError = {
          message: payload.message || 'Failed to fetch accounts',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        };
        state.allTeams.loading = false;
        state.allTeams.error = error;
        state.personalAccount.loading = false;
        state.personalAccount.error = error;
      })

      // Create a new teams
      .addCase(createTeam.pending, (state) => {
        state.newTeam.record = null;
        state.newTeam.loading = true;
        state.newTeam.error = null;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        const newTeamData = action.payload.data;
        const newTeam = _formatTeamObject(newTeamData as Team);
        state.newTeam.loading = false;
        state.newTeam.record = newTeam;
        state.allTeams.records = [...state.allTeams.records, newTeam];
      })
      .addCase(createTeam.rejected, (state, action) => {
        const payload = (action.payload || {}) as { message?: string };
        state.newTeam.loading = false;
        state.newTeam.error = {
          message: payload.message || 'Failed to create team',
          type: ErrorType.UNKNOWN,
          isNetworkError: false,
        };
      })

      // Delete a team
      .addCase(deleteTeam.pending, (state, action) => {
        const _team = action.meta.arg.team;
        state.deletedTeamsById[_team.id] = {
          record: _team,
          loading: true,
          error: null,
        };
      })
      .addCase(deleteTeam.fulfilled, (state, action) => {
        const _team = action.meta.arg.team;
        delete state.deletedTeamsById[_team.id];
        state.allTeams.records = state.allTeams.records.filter(
          (team) => team.id !== _team.id
        );
        if (state.currentTeam.record?.id === _team.id) {
          _setCurrentAccount(state, {
            payload: null,
            type: 'team/setCurrentTeam',
          } as PayloadAction<Team | null>);
        }
      })
      .addCase(deleteTeam.rejected, (state, action) => {
        const _team = action.meta.arg.team;
        state.deletedTeamsById[_team.id] = {
          record: _team,
          loading: false,
          error: {
            message:
              ((action.payload || {}) as { message?: string }).message ||
              'Failed to delete team',
            type: ErrorType.UNKNOWN,
            isNetworkError: false,
          },
        };
      })

      // Rename a team
      .addCase(renameTeam.pending, (state, action) => {
        const _team = action.meta.arg.team;
        state.renamedTeamsById[_team.id] = {
          record: _team,
          loading: true,
          error: null,
        };
      })
      .addCase(renameTeam.fulfilled, (state, action) => {
        const _team = action.meta.arg.team;
        const newName = action.meta.arg.newName;
        delete state.renamedTeamsById[_team.id];

        // Update the team name in allTeams
        const teamIndex = state.allTeams.records.findIndex(
          (team) => team.id === _team.id
        );
        if (teamIndex !== -1) {
          state.allTeams.records[teamIndex].cirra_organization_name = newName;
        }

        // Update current team if it's the one being renamed
        if (state.currentTeam.record?.id === _team.id) {
          state.currentTeam.record.cirra_organization_name = newName;
        }
      })
      .addCase(renameTeam.rejected, (state, action) => {
        const _team = action.meta.arg.team;
        state.renamedTeamsById[_team.id] = {
          record: _team,
          loading: false,
          error: {
            message:
              ((action.payload || {}) as { message?: string }).message ||
              'Failed to rename team',
            type: ErrorType.UNKNOWN,
            isNetworkError: false,
          },
        };
      })

      // Handle selectAccount for team switching
      .addCase(selectAccount.pending, (state) => {
        state.isAccountSwitching = true;
      })
      .addCase(selectAccount.fulfilled, (state) => {
        state.isAccountSwitching = false;
      })
      .addCase(selectAccount.rejected, (state) => {
        state.isAccountSwitching = false;
      });
  },
});

export const { setCurrentTeam } = teamSlice.actions;

/**
 * Returns true when a team-only user has no accessible teams.
 * Used to block the app and show a "no access" screen.
 */
export const selectIsTeamOnlyWithNoTeams = (state: {
  team: OrganizationState;
}) =>
  state.team.personalAccount.record?.team_only === true &&
  !state.team.allTeams.records.some((t) => Boolean(t.current_user));

export default teamSlice.reducer;
